import * as Sentry from '@sentry/react';
import type { User } from 'features/users/types';
import { VideoPipRenderer } from 'utils/pip/video-pip/VideoPipRenderer';
import { PipRenderer } from 'utils/pip/PipRendererer';
import { logger } from 'utils/logger';
import { clearVideo } from 'utils/video';
import { PipEntryPoint } from 'utils/pip/types';
import { extractPipRequestState } from 'utils/pip/pipRequestDiagnostics';

type AnimationId = number | null;

export type VideoPipSettings = {
  showPlaceholder: boolean;
  active: boolean;
  muted: boolean;
};

const REQUEST_STALL_TIMEOUT_MS = 10000;

export class VideoPipManagerClass {
  private renderer: PipRenderer<VideoPipSettings>;

  private animation: AnimationId = null;

  private video: HTMLVideoElement | null = null;

  private stream: MediaStream | null = null;

  private active: boolean = false;

  private requesting: boolean = false;

  constructor(renderer: VideoPipRenderer) {
    this.renderer = renderer;
  }

  async prepare(width: number, height: number, user: User, settings: VideoPipSettings) {
    if (this.active || this.video) {
      return;
    }

    const video = this.build(width, height, user, settings);

    video.playsInline = true;
    video.style.cssText =
      'position:fixed;bottom:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none';

    document.body.appendChild(video);

    try {
      await video.play();
    } catch (error) {
      if (this.video !== video) {
        return;
      }

      logger.remote({ capture: 'pip' }).warn('[VideoPipManager] prepare failed:', error);
      this.stop();
      return;
    }

    if (this.video !== video || this.active) {
      return;
    }

    this.stopDrawing();
  }

  async requestWindow(
    width: number,
    height: number,
    user: User,
    settings: VideoPipSettings,
    entry: PipEntryPoint
  ): Promise<HTMLVideoElement | null> {
    if (this.requesting) {
      logger.remote({ capture: 'pip' }).warn('[VideoPipManager] PiP request already in flight');
      return null;
    }

    const preparedVideo = this.video;

    this.requesting = true;
    this.active = true;

    try {
      let video = preparedVideo;
      let playDurationMs = 0;

      if (video) {
        this.renderer.init({ user, settings });
      } else {
        video = this.build(width, height, user, settings);

        const playStartedAt = performance.now();
        await video.play();
        playDurationMs = Math.round(performance.now() - playStartedAt);
      }

      const diagnostics = extractPipRequestState(video, {
        entry,
        prepared: preparedVideo != null,
        playDurationMs,
      });

      logger.remote({ capture: 'pip' }).info('[VideoPipManager] requesting PiP', diagnostics);

      let stallTimeout = 0;

      if (preparedVideo) {
        stallTimeout = window.setTimeout(() => {
          logger
            .remote({ capture: 'pip' })
            .warn('[VideoPipManager] requestPictureInPicture has not settled', {
              afterMs: REQUEST_STALL_TIMEOUT_MS,
            });

          this.requesting = false;
          this.stop();
        }, REQUEST_STALL_TIMEOUT_MS);
      }

      try {
        await video.requestPictureInPicture();
      } catch (error) {
        logger
          .remote({ capture: 'pip' })
          .warn('[VideoPipManager] requestPictureInPicture failed:', error);
        Sentry.captureException(error, {
          tags: { 'pip.entry': entry },
          contexts: { pipRequest: diagnostics },
        });
        this.stop();
        return null;
      } finally {
        window.clearTimeout(stallTimeout);
      }

      if (preparedVideo) {
        this.startDrawing();
      }

      return video;
    } finally {
      this.requesting = false;
    }
  }

  private build(width: number, height: number, user: User, settings: VideoPipSettings) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const stream = canvas.captureStream(30);
    const video = document.createElement('video');

    video.srcObject = stream;
    video.controls = false;
    video.muted = true;

    this.video = video;
    this.stream = stream;

    this.renderer.init({
      ctx: canvas.getContext('2d')!,
      user,
      settings,
    });

    video.addEventListener('play', () => {
      this.startDrawing();
    });

    return video;
  }

  private startDrawing() {
    this.stopDrawing();

    this.animation = this.renderer.draw();
  }

  private stopDrawing() {
    if (this.animation) {
      cancelAnimationFrame(this.animation);
      this.animation = null;
    }
  }

  redraw(user: User, settings: VideoPipSettings) {
    if (!this.isActive()) {
      return;
    }

    this.renderer.init({
      user,
      settings,
    });

    this.startDrawing();
  }

  stop() {
    this.active = false;

    if (this.video && document.pictureInPictureElement === this.video) {
      document.exitPictureInPicture();
    }

    this.stopDrawing();

    if (this.video) {
      clearVideo(this.video);
      this.video.remove();
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }

    this.video = null;
    this.stream = null;

    this.renderer.reset();
  }

  isActive() {
    return this.active;
  }
}

export const VideoPipManager = new VideoPipManagerClass(new VideoPipRenderer());
